import type { PhoneTreeOption } from './types';

export const MAX_PHONE_TREE_OPTIONS = 10;
export const MAX_PHONE_TREE_GREETING_LENGTH = 1000;

export const PHONE_TREE_KEYS = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
] as const;

/** The key a caller presses: the admin's choice, else the 1-based position (the tenth is 0). */
export const optionKey = (option: PhoneTreeOption, index: number): string =>
  option.key ?? String((index + 1) % 10);

export const duplicateKeys = (options: PhoneTreeOption[]): Set<string> => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  options.forEach((option, index) => {
    const key = optionKey(option, index);
    if (seen.has(key)) duplicates.add(key);
    seen.add(key);
  });
  return duplicates;
};

/** True when the repeat key is already the key of one of the options. */
export const repeatKeyCollides = (
  options: PhoneTreeOption[],
  repeatKey: string | undefined
): boolean =>
  !!repeatKey &&
  options.some((option, index) => optionKey(option, index) === repeatKey);

const SPOKEN_REPEAT_KEYS: Record<string, string> = {
  '*': 'star',
  '#': 'pound',
};

/** The spoken menu; mirrors build_menu_config's menu_speech on the backend. */
export const menuSpeech = (
  options: PhoneTreeOption[],
  repeatKey?: string
): string => {
  const list = options
    .map(
      (option, index) =>
        `${optionKey(option, index)} for ${option.label || 'Untitled'}`
    )
    .join(', press ');
  const repeat = repeatKey
    ? `, or press ${SPOKEN_REPEAT_KEYS[repeatKey] ?? repeatKey} to hear this menu again`
    : '';
  return `Press ${list}${repeat}.`;
};

/**
 * Lowest key not already used, for a newly added option. The repeat key counts
 * as used. Returns '' when no digit is free (nine options plus a digit repeat
 * key), which the UI treats as "cannot add another key".
 */
export const lowestUnusedKey = (
  options: PhoneTreeOption[],
  repeatKey?: string
): string => {
  const used = new Set(options.map(optionKey));
  if (repeatKey) used.add(repeatKey);
  return PHONE_TREE_KEYS.find((key) => !used.has(key)) ?? '';
};
